import { api } from './api';
import { chatTenantService } from './chatTenantService';

// Tipos baseados no Copiloto
export interface NewChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: DocumentSource[];
  close?: boolean;
  error?: string | null;
}

export interface DocumentSource {
  id: string;
  documentId?: string;
  documentContentId?: string;
  title: string;
  path?: string;
  chunk: string;
  score: number;
  metadata?: {
    ContentType?: string;
    Author?: string;
    FileName?: string;
    Size?: number;
    CreatedAt?: string;
    UpdatedAt?: string;
  };
}

export interface StreamChatOptions {
  threadId?: string;
  /** Obrigatório quando não há thread (primeira mensagem); omitido quando `threadId` está presente. */
  workspaceId?: string;
  /** Com `threadId`: RAG/config do workspace alvo; mensagens continuam na thread atual. */
  targetWorkspaceId?: string;
  /** Texto persistido como mensagem do usuário; padrão é `message`. */
  userMessageContent?: string;
  message: string;
  attachments?: ChatVisionAttachmentRef[];
  onChunk: (chunk: StreamChunk) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
  signal?: AbortSignal;
}

export interface ChatVisionAttachmentRef {
  fileName: string;
  fileType: string;
  fileSize: number;
  filePath: string;
  previewUrl?: string;
}

export interface StreamChunk {
  id?: string;
  uuid?: string;
  type?: string;
  textResponse?: string;
  sources?: DocumentSource[];
  close?: boolean;
  error?: string | null;
  action?: string;
  thread?: {
    slug: string;
    name: string;
  };
}

export interface ChatHistory {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  sources?: DocumentSource[];
}

export interface WorkspaceThread {
  id: string;
  slug: string;
  name: string;
  workspaceId: string;
  createdAt: string;
  updatedAt: string;
}

class NewChatInDocService {
  private baseUrl = process.env.REACT_APP_API_URL || '';

  /**
   * Stream de chat via backend (que chama Copiloto)
   */
  async streamChat(options: StreamChatOptions): Promise<void> {
    const { threadId, workspaceId, targetWorkspaceId, userMessageContent, message, attachments, onChunk, onError, onComplete, signal } = options;

    const endpoint = `${this.baseUrl}/chatindoc/stream-chat`;

    const ctrl = new AbortController();
    
    // Se um signal foi fornecido, vincular o abort
    if (signal) {
      signal.addEventListener('abort', () => {
        ctrl.abort();
        onChunk({
          id: crypto.randomUUID(),
          type: 'stopGeneration',
        });
      });
    }

    try {
      console.log('=== FRONTEND: Iniciando streamChat ===');
      console.log('Endpoint:', endpoint);
      console.log('Message:', message);
      console.log('ThreadId:', threadId);
      console.log('WorkspaceId:', workspaceId);
      
      // Obter token do sessionStorage (mesmo que o api usa)
      const hybridToken = sessionStorage.getItem('@KnowInDoc:accessToken');
      const customerToken = sessionStorage.getItem('@KnowInDoc:customerToken');
      const userToken = sessionStorage.getItem('@KnowInDoc:userToken');
      const customerId = sessionStorage.getItem('@KnowInDoc:customer_id');
      const userId = sessionStorage.getItem('@KnowInDoc:user_id');

      console.log('Tokens obtidos:', { 
        hasHybridToken: !!hybridToken, 
        hasCustomerToken: !!customerToken, 
        hasUserToken: !!userToken,
        customerId,
        userId 
      });

      // Usar fetch com streaming e autenticação correta
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': hybridToken ? `Bearer ${hybridToken}` : '',
          'customer_token': customerToken || '',
          'user_token': userToken || '',
          'customer_id': customerId || '',
          'user_id': userId || '',
        },
        body: JSON.stringify(
          this.buildStreamChatBody(message, threadId, workspaceId, targetWorkspaceId, userMessageContent, attachments)
        ),
        signal: ctrl.signal,
      });

      console.log('Resposta recebida:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is null');
      }

      console.log('Reader obtido, iniciando leitura do stream...');
      const decoder = new TextDecoder();
      let chunkCount = 0;
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('Stream finalizado (done=true)');
          break;
        }
        
        chunkCount++;
        const chunk = decoder.decode(value, { stream: true });
        console.log(`Chunk ${chunkCount}:`, chunk);
        
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = line.slice(6); // Remove 'data: '
              if (data.trim() === '') continue;
              
              console.log('Dados recebidos:', data);
              const chatResult: StreamChunk = JSON.parse(data);
              console.log('ChatResult parseado:', chatResult);
              
              console.log('🚀 CHAMANDO onChunk com:', chatResult);
              onChunk(chatResult);
              console.log('✅ onChunk executado');
              
              if (chatResult.close) {
                console.log('ChatResult com close=true, finalizando stream');
                reader.cancel();
                return;
              }
            } catch (error) {
              console.error('Erro ao parsear mensagem:', error, 'Data:', line.slice(6));
            }
          }
        }
      }

      console.log('Stream processado completamente');
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Erro no streamChat:', error);
      if (onError && error instanceof Error) {
        onError(error);
      }
    }
  }

  private buildStreamChatBody(
    message: string,
    threadId: string | undefined,
    workspaceId: string | undefined,
    targetWorkspaceId: string | undefined,
    userMessageContent: string | undefined,
    attachments: ChatVisionAttachmentRef[] | undefined
  ): {
    message: string;
    attachments: ChatVisionAttachmentRef[];
    threadId?: string;
    workspaceId?: string;
    targetWorkspaceId?: string;
    userMessageContent?: string;
  } {
    const hasThread = Boolean(threadId?.trim());
    const body: {
      message: string;
      attachments: ChatVisionAttachmentRef[];
      threadId?: string;
      workspaceId?: string;
      targetWorkspaceId?: string;
      userMessageContent?: string;
      customerId?: string;
    } = {
      message,
      attachments: (attachments ?? []).map((item) => ({
        fileName: item.fileName,
        fileType: item.fileType,
        fileSize: item.fileSize,
        filePath: item.filePath,
      })),
      ...chatTenantService.buildCustomerIdParams(),
    };
    if (hasThread) {
      body.threadId = threadId;
    } else if (workspaceId?.trim()) {
      body.workspaceId = workspaceId.trim();
    }
    if (targetWorkspaceId?.trim()) {
      body.targetWorkspaceId = targetWorkspaceId.trim();
    }
    if (userMessageContent != null && userMessageContent !== message) {
      body.userMessageContent = userMessageContent;
    }
    return body;
  }

  /**
   * Busca histórico de uma thread específica (usando endpoint existente)
   */
  async getThreadChatHistory(threadId: string): Promise<ChatHistory[]> {
    try {
      const response = await api.get(`/chatindoc/threads/${threadId}/history`);
      return response.data || [];
    } catch (error) {
      console.error('Erro ao buscar histórico da thread:', error);
      return [];
    }
  }

  /**
   * Busca threads de um workspace (usando endpoint existente)
   */
  async getWorkspaceThreads(workspaceId: string): Promise<WorkspaceThread[]> {
    try {
      const response = await api.get(`/chatindoc/workspaces/${workspaceId}/threads`);
      
      // Converter DTOs do backend para formato esperado
      return response.data.map((thread: any) => ({
        id: thread.id,
        name: thread.title,
        slug: thread.slug,
        workspaceId: workspaceId,
        createdAt: thread.createdAt,
        updatedAt: thread.updatedAt
      })) || [];
    } catch (error) {
      console.error('Erro ao buscar threads:', error);
      return [];
    }
  }

  /**
   * Cria uma nova thread (usando endpoint existente)
   */
  async createThread(workspaceId: string, title: string): Promise<WorkspaceThread | null> {
    try {
      const response = await api.post('/chatindoc/threads', {
        workspaceId,
        title,
        ...chatTenantService.buildCustomerIdParams(),
      });

      // Converter resposta do backend para formato esperado
      return {
        id: response.data.threadId,
        name: response.data.title,
        slug: response.data.threadId, // Usar threadId como slug
        workspaceId: workspaceId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Erro ao criar thread:', error);
      return null;
    }
  }

  /**
   * Deleta uma thread (usando endpoint existente)
   */
  async deleteThread(threadId: string): Promise<boolean> {
    try {
      await api.delete(`/chatindoc/threads/${threadId}`);
      return true;
    } catch (error) {
      console.error('Erro ao deletar thread:', error);
      return false;
    }
  }

  /**
   * Busca documentos indexados usando a tabela DocumentChunk existente
   * Esta função usa o endpoint de busca vetorial já existente no backend
   */
  async searchDocuments(query: string, maxResults: number = 4): Promise<DocumentSource[]> {
    try {
      const response = await api.post('/chatindoc/search', {
        message: query,
        maxResults,
        similarityThreshold: 0.25,
        ...chatTenantService.buildCustomerIdParams(),
      });

      if (response.data && response.data.sources) {
        // Mapear os sources do formato ChatInDoc para DocumentSource
        return response.data.sources.map((source: any) => ({
          id: source.documentContentId,
          documentId: source.documentId,
          documentContentId: source.documentContentId,
          title: source.title,
          path: source.path,
          chunk: source.chunks && source.chunks.length > 0 
            ? source.chunks.map((c: any) => c.text).join('\n\n')
            : '',
          score: source.score,
          metadata: {
            ContentType: source.metadata?.contentType,
            Author: source.author,
            FileName: source.title,
            Size: source.metadata?.size || 0,
            CreatedAt: source.metadata?.createdAt,
            UpdatedAt: source.metadata?.updatedAt,
          },
        }));
      }

      return [];
    } catch (error) {
      console.error('Erro ao buscar documentos:', error);
      return [];
    }
  }
}

export const newChatInDocService = new NewChatInDocService();

